/**
 * AdminIonKnowledgeReviewSubtab — the approval queue for Ion knowledge.
 *
 * Non-trusted admins ("authors") submit knowledge edits that land as PENDING
 * proposals instead of mutating the live `ion_knowledge_entries` the AI
 * serves. Approvers review them here: a diff vs. the current live entry, then
 * Approve (promote to live) or Reject (with a note). Approvers also manage who
 * is an approver / trusted editor.
 *
 * Visible only to approvers (superusers are implicitly approvers). The gate is
 * enforced server-side; this component just reflects it.
 */
import { useEffect, useMemo, useState } from 'preact/hooks'
import { createPortal } from 'preact/compat'
import {
  getKnowledgeCapabilities,
  listKnowledgeProposals,
  approveKnowledgeProposal,
  rejectKnowledgeProposal,
  listKnowledgeEditors,
  setKnowledgeEditorFlags,
} from '../../services/ionKnowledgeService.js'

function formatDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
}

const ACTION_LABEL = {
  create: 'New entry',
  update: 'Edit',
  deactivate: 'Soft-delete',
}

export function AdminIonKnowledgeReviewSubtab() {
  const [caps, setCaps] = useState(null)
  const [proposals, setProposals] = useState([])
  const [editors, setEditors] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [reviewing, setReviewing] = useState(null) // proposal object | null

  const refresh = async () => {
    setLoading(true)
    setError(null)
    try {
      const c = await getKnowledgeCapabilities()
      setCaps(c)
      if (c.is_approver) {
        const [p, e] = await Promise.all([
          listKnowledgeProposals({ status: 'pending' }),
          listKnowledgeEditors(),
        ])
        setProposals(p.proposals || [])
        setEditors(e.editors || [])
      }
    } catch (e) {
      setError(e?.message || 'network')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [])

  if (loading && caps === null) {
    return (
      <div className="admin-aim-subtab-body">
        <div className="admin-aim-empty">Lade…</div>
      </div>
    )
  }

  if (caps && !caps.is_approver) {
    return (
      <div className="admin-aim-subtab-body">
        <header className="admin-aim-header">
          <div>
            <h2 className="admin-aim-title">Knowledge Review</h2>
            <p className="admin-aim-sub">
              Only knowledge approvers can review the proposal queue. Your edits
              in the Knowledge tab are submitted here for an approver to publish.
            </p>
          </div>
        </header>
      </div>
    )
  }

  return (
    <div className="admin-aim-subtab-body">
      <header className="admin-aim-header">
        <div>
          <h2 className="admin-aim-title">Knowledge Review</h2>
          <p className="admin-aim-sub">
            Pending knowledge changes from authors. Approving promotes the change
            to the live entry the AI serves; rejecting keeps a note. Trusted
            editors and approvers publish directly and never appear here.
          </p>
        </div>
        <button type="button" className="admin-ai-btn admin-ai-btn--ghost" onClick={refresh}>
          ↻
        </button>
      </header>

      {error && <div className="admin-aim-error-banner">{error}</div>}

      <h3 className="admin-aim-section-title">
        Pending review <span className="admin-aim-meta">({proposals.length})</span>
      </h3>
      <div className="admin-aim-table-wrap">
        <table className="admin-aim-table">
          <thead>
            <tr>
              <th>Action</th>
              <th>Topic</th>
              <th>Author</th>
              <th>Base → Live</th>
              <th>Submitted</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {proposals.length === 0 ? (
              <tr>
                <td colSpan={6} className="admin-aim-empty">No pending proposals.</td>
              </tr>
            ) : (
              proposals.map((p) => (
                <tr key={p.id}>
                  <td>
                    <span className={`admin-aim-proposal-action is-${p.action}`}>
                      {ACTION_LABEL[p.action] || p.action}
                    </span>
                  </td>
                  <td>
                    <code>{p.topic_id || p.proposed_topic_id}</code>
                    {(p.topic_id || p.proposed_topic_id || '').endsWith('_selection') && (
                      <span className="admin-aim-flow-tag">AI Flow</span>
                    )}
                    {p.target_missing && (
                      <span className="admin-aim-proposal-warn"> · target gone</span>
                    )}
                  </td>
                  <td>{p.author_email}</td>
                  <td>
                    {p.action === 'create' ? (
                      '—'
                    ) : p.conflict ? (
                      <span className="admin-aim-proposal-warn">
                        ⚠ v{p.base_version} → v{p.current_version}
                      </span>
                    ) : (
                      `v${p.base_version ?? '—'}`
                    )}
                  </td>
                  <td className="admin-aim-rel">{formatDate(p.created_at)}</td>
                  <td>
                    <button
                      type="button"
                      className="admin-ai-btn admin-ai-btn--ghost"
                      onClick={() => setReviewing(p)}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <EditorsPanel editors={editors} onChanged={refresh} />

      {reviewing && (
        <ProposalReviewModal
          proposal={reviewing}
          onClose={() => setReviewing(null)}
          onDone={() => {
            setReviewing(null)
            refresh()
          }}
        />
      )}
    </div>
  )
}

function DiffField({ label, current, proposed, tall = false }) {
  const changed = (current ?? '') !== (proposed ?? '')
  if (proposed == null) return null
  const preCls = `admin-aim-diff-pre${tall ? ' admin-aim-diff-pre--tall' : ''}`
  return (
    <div className={`admin-aim-diff-field ${changed ? 'is-changed' : ''}`}>
      <div className="admin-aim-diff-label">{label}{changed ? ' · changed' : ''}</div>
      <div className="admin-aim-diff-cols">
        <div className="admin-aim-diff-col">
          <div className="admin-aim-diff-col-head">Current (live)</div>
          <pre className={preCls}>{current || '—'}</pre>
        </div>
        <div className="admin-aim-diff-col">
          <div className="admin-aim-diff-col-head">Proposed</div>
          <pre className={preCls}>{proposed || '—'}</pre>
        </div>
      </div>
    </div>
  )
}

function ProposalReviewModal({ proposal, onClose, onDone }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const cur = proposal.current || {}
  const prop = proposal.proposed || {}

  const handleApprove = async () => {
    if (proposal.conflict) {
      if (
        !window.confirm(
          `The live entry changed since this proposal (base v${proposal.base_version} → ` +
            `live v${proposal.current_version}). Approve anyway and overwrite with the ` +
            `proposed content?`,
        )
      )
        return
    }
    setBusy(true)
    setError(null)
    try {
      await approveKnowledgeProposal(proposal.id, { force: !!proposal.conflict })
      onDone()
    } catch (e) {
      setError(e?.message || 'approve failed')
    } finally {
      setBusy(false)
    }
  }

  const handleReject = async () => {
    const note = window.prompt('Reason for rejecting (optional, shown to the author):', '')
    if (note === null) return // cancelled
    setBusy(true)
    setError(null)
    try {
      await rejectKnowledgeProposal(proposal.id, note)
      onDone()
    } catch (e) {
      setError(e?.message || 'reject failed')
    } finally {
      setBusy(false)
    }
  }

  return createPortal(
    <div className="admin-aim-drawer-overlay" onClick={onClose}>
      <div
        className="admin-aim-drawer admin-aim-drawer--wide"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="admin-aim-drawer-header">
          <div className="admin-aim-drawer-headinfo">
            <div className="admin-aim-drawer-title">
              {ACTION_LABEL[proposal.action] || proposal.action} ·{' '}
              {proposal.topic_id || proposal.proposed_topic_id}
            </div>
            <div className="admin-aim-drawer-sub">
              by {proposal.author_email} · submitted {formatDate(proposal.created_at)}
            </div>
          </div>
          <button type="button" className="admin-aim-icon-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="admin-aim-drawer-body">
          {proposal.conflict && (
            <div className="admin-aim-warning">
              ⚠ The live entry changed since this proposal was made (base v
              {proposal.base_version} → live v{proposal.current_version}). Approving
              will overwrite the live content with what's proposed here.
            </div>
          )}
          {proposal.action === 'deactivate' ? (
            <div className="admin-aim-admin-note">
              This proposal soft-deletes <code>{proposal.topic_id}</code> — it will
              disappear from the AI's index but stay in the database.
            </div>
          ) : (
            <>
              <DiffField label="Title" current={cur.title} proposed={prop.title} />
              <DiffField label="Summary" current={cur.summary} proposed={prop.summary} tall />
              <DiffField label="Body (markdown)" current={cur.body_md} proposed={prop.body_md} tall />
              <DiffField label="Keywords" current={cur.keywords} proposed={prop.keywords} />
              <DiffField label="applies_to" current={cur.applies_to} proposed={prop.applies_to} />
            </>
          )}
          {error && <div className="admin-aim-error-banner">{error}</div>}
        </div>

        <footer className="admin-aim-drawer-footer">
          <button
            type="button"
            className="admin-ai-btn admin-ai-btn--danger"
            onClick={handleReject}
            disabled={busy}
          >
            Reject
          </button>
          <span className="admin-aim-spacer" />
          <button
            type="button"
            className="admin-ai-btn admin-ai-btn--ghost"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button type="button" className="admin-ai-btn" onClick={handleApprove} disabled={busy}>
            {busy ? 'Working…' : 'Approve & publish'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  )
}

function EditorsPanel({ editors, onChanged }) {
  const [savingId, setSavingId] = useState(null)
  const [error, setError] = useState(null)

  const toggle = async (ed, field) => {
    setSavingId(ed.user_id)
    setError(null)
    try {
      await setKnowledgeEditorFlags(ed.user_id, { [field]: !ed[field] })
      onChanged()
    } catch (e) {
      setError(e?.message || 'update failed')
    } finally {
      setSavingId(null)
    }
  }

  return (
    <div className="admin-aim-editors">
      <h3 className="admin-aim-section-title">Editors &amp; capabilities</h3>
      <p className="admin-aim-sub">
        <strong>Approver</strong> reviews this queue and publishes directly.
        <strong> Trusted editor</strong> publishes directly without review.
        Everyone else is an author whose edits queue here. Superusers are always
        approvers.
      </p>
      {error && <div className="admin-aim-error-banner">{error}</div>}
      <div className="admin-aim-table-wrap">
        <table className="admin-aim-table">
          <thead>
            <tr>
              <th>Admin</th>
              <th>Approver</th>
              <th>Trusted editor</th>
              <th>Publishes directly</th>
            </tr>
          </thead>
          <tbody>
            {editors.map((ed) => (
              <tr key={ed.user_id}>
                <td>
                  {ed.email}
                  {ed.is_implicit_approver && (
                    <span className="admin-aim-state-pill is-admin"> ADMIN</span>
                  )}
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={ed.ai_approver || ed.is_implicit_approver}
                    disabled={ed.is_implicit_approver || savingId === ed.user_id}
                    title={ed.is_implicit_approver ? 'Full admins are always approvers' : ''}
                    onChange={() => toggle(ed, 'ai_approver')}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={ed.ai_trusted_editor}
                    disabled={savingId === ed.user_id}
                    onChange={() => toggle(ed, 'ai_trusted_editor')}
                  />
                </td>
                <td>{ed.can_publish_directly ? '✓' : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
